import React, { useState, useEffect } from 'react';
import {
  Box, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Alert, CircularProgress, Typography, Grid, Chip, Tabs, Tab, MenuItem
} from '@mui/material';
import { Add as AddIcon, Refresh as RefreshIcon, Check as ApproveIcon, Close as RejectIcon, Visibility as ViewIcon, PhotoCamera as PhotoIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import {
  getPartRequests, createPartRequest, approvePartRequest, rejectPartRequest, deletePartRequest, getRepairs
} from '../../services/api';
import { getErrorMessage } from '../../utils/errorMessage';
import { getUserRole, isTechnician, canDelete } from '../../utils/auth';
import { printPartRequestsPdf } from '../../utils/partRequestsPdfExport';
import PartPhotoImage from '../../components/PartPhotoImage';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ExportButtons from '../../components/ExportButtons';
import { dash, fmtExportMoney, fmtExportStatus } from '../../utils/tableExport';

const statusColor = (status) => {
  const map = { pending: 'warning', approved: 'success', issued: 'info', rejected: 'error', price_approved: 'primary' };
  return map[status] || 'default';
};

const PartRequests = () => {
  const userRole = getUserRole();
  const techOnly = isTechnician();
  const canApprove = ['admin', 'super_admin', 'branch_manager', 'manager', 'technical_coordinator', 'admin_officer'].includes(userRole);
  const canRequest = [
    'admin', 'technician', 'branch_manager', 'branch_staff', 'manager',
    'staff', 'super_admin', 'technical_coordinator', 'admin_officer'
  ].includes(userRole);

  const [tab, setTab] = useState(0);
  const [sourceFilter, setSourceFilter] = useState('all');
  const [requests, setRequests] = useState([]);
  const [repairs, setRepairs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [createDialog, setCreateDialog] = useState(false);
  const [approveDialog, setApproveDialog] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({
    repair_id: '',
    product_name: '',
    product_code: '',
    quantity: 1,
    unit_cost: 0,
    unit_price: 0,
    part_photo: ''
  });
  const [photoName, setPhotoName] = useState('');
  const [approveForm, setApproveForm] = useState({ approved_unit_price: 0, admin_notes: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadData();
    const refresh = () => loadData();
    window.addEventListener('part-request-created', refresh);
    return () => window.removeEventListener('part-request-created', refresh);
  }, [tab, sourceFilter]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');
      const params = new URLSearchParams();
      if (tab === 1) params.set('status', 'pending');
      if (tab === 2) params.set('status', 'approved');
      if (sourceFilter && sourceFilter !== 'all') params.set('source', sourceFilter);
      const query = params.toString() ? `?${params.toString()}` : '';

      try {
        const rRes = await getPartRequests(query);
        setRequests(rRes.data || []);
      } catch (err) {
        setError(getErrorMessage(err, 'Failed to load part requests'));
        setRequests([]);
      }

      try {
        const repRes = await getRepairs('?limit=100');
        setRepairs(repRes.data || []);
      } catch {
        setRepairs([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoPick = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file (JPG/PNG)');
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      setError('Part photo must be under 8MB');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setForm((prev) => ({ ...prev, part_photo: reader.result }));
      setPhotoName(file.name);
    };
    reader.readAsDataURL(file);
  };

  const resetCreateForm = () => {
    setForm({
      repair_id: '',
      product_name: '',
      product_code: '',
      quantity: 1,
      unit_cost: 0,
      unit_price: 0,
      part_photo: ''
    });
    setPhotoName('');
  };

  const handleCreate = async () => {
    try {
      if (!form.repair_id) {
        setError('Select a repair job');
        return;
      }
      if (!form.product_name.trim()) {
        setError('Part name is required');
        return;
      }
      if (techOnly && !form.part_photo) {
        setError('Part photo is required');
        return;
      }
      setSubmitting(true);
      await createPartRequest({
        repair_id: parseInt(form.repair_id, 10),
        product_name: form.product_name,
        product_code: form.product_code,
        quantity: form.quantity,
        unit_cost: form.unit_cost,
        unit_price: form.unit_price,
        part_photo: form.part_photo || undefined,
        source: 'repair'
      });
      setCreateDialog(false);
      resetCreateForm();
      setSuccess('Part request submitted for approval');
      window.dispatchEvent(new Event('part-request-created'));
      loadData();
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to submit part request'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleApprove = async () => {
    try {
      await approvePartRequest(selected.id, approveForm);
      setApproveDialog(false);
      setSuccess('Part request approved');
      loadData();
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to approve part request'));
    }
  };

  const handleReject = async (item) => {
    try {
      await rejectPartRequest(item.id, { admin_notes: 'Rejected by admin' });
      setSuccess('Part request rejected');
      loadData();
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to reject part request'));
    }
  };

  const openApprove = (item) => {
    setSelected(item);
    setApproveForm({ approved_unit_price: item.unit_price || 0, admin_notes: '' });
    setApproveDialog(true);
  };

  const jobLabel = (req) => {
    if (req.source === 'ssi') {
      if (req.ssi_job_id) return `SSI #${req.ssi_job_id}`;
      return 'SSI Technical';
    }
    if (req.service_number) return req.service_number;
    if (req.repair?.grn_number) return req.repair.grn_number;
    if (req.repair?.service_number) return req.repair.service_number;
    if (req.repair?.repair_number) return req.repair.repair_number;
    const r = repairs.find((x) => String(x.id) === String(req.repair_id));
    return r ? (r.grn_number || r.service_number || r.repair_number || `#${r.id}`) : `#${req.repair_id}`;
  };

  const viewLink = (r) => {
    if (r.source === 'ssi' && r.ssi_job_id) return `/ssi-technical/${r.ssi_job_id}`;
    if (r.repair_id) return `/repairs/${r.repair_id}`;
    return null;
  };

  const handleDelete = async (r) => {
    if (!canDelete()) return;
    if (!window.confirm(`Delete part request "${r.product_name}"?`)) return;
    try {
      await deletePartRequest(r.id);
      setSuccess('Part request deleted');
      loadData();
    } catch (err) {
      setError(getErrorMessage(err, 'Delete failed'));
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={600}>🔩 Part Requests</Typography>
          <Typography color="text.secondary">
            Technicians can request parts with a photo — admin approves parts and price
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            select
            size="small"
            label="Source"
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value)}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="repair">Repair Job</MenuItem>
            <MenuItem value="ssi">SSI Technical</MenuItem>
          </TextField>
          <Button startIcon={<RefreshIcon />} onClick={loadData}>Refresh</Button>
          <ExportButtons
            title="Part Requests"
            filename="part_requests"
            landscape
            columns={[
              { label: 'Source', value: (r) => (r.source === 'ssi' ? 'SSI' : 'Repair Job') },
              { label: 'Job', value: (r) => dash(jobLabel(r)) },
              { label: 'Customer', value: (r) => dash(r.repair?.customer_name) },
              { label: 'Part', value: (r) => `${r.product_name || '-'}${r.product_code ? ` (${r.product_code})` : ''}` },
              { label: 'Qty', value: (r) => r.quantity ?? 1 },
              { label: 'Requested Price', align: 'right', value: (r) => fmtExportMoney(r.unit_price) },
              { label: 'Approved Price', align: 'right', value: (r) => (r.approved_unit_price != null ? fmtExportMoney(r.approved_unit_price) : '-') },
              { label: 'Requested By', value: (r) => dash(r.requested_by_name) },
              { label: 'Status', value: (r) => fmtExportStatus(r.status) }
            ]}
            rows={requests}
            onError={setError}
          />
          <Button
            variant="outlined"
            startIcon={<PictureAsPdfIcon />}
            disabled={!requests.length}
            onClick={() => {
              try {
                printPartRequestsPdf(requests, { title: 'Part Requests' });
              } catch (err) {
                setError(err.message || 'Failed to export PDF');
              }
            }}
          >
            Export PDF with Photos
          </Button>
          {canRequest && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateDialog(true)}>Request Part</Button>
          )}
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="All Requests" />
        <Tab label="Pending Approval" />
        <Tab label="Approved" />
      </Tabs>

      {loading ? <CircularProgress /> : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Source</TableCell>
                <TableCell>Job</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell>Part</TableCell>
                <TableCell>Photo</TableCell>
                <TableCell>Qty</TableCell>
                <TableCell>Requested Price</TableCell>
                <TableCell>Approved Price</TableCell>
                <TableCell>Requested By</TableCell>
                <TableCell>Status</TableCell>
                {canApprove && <TableCell>Actions</TableCell>}
                <TableCell>View</TableCell>
                {canDelete() && <TableCell>Delete</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {requests.map((r) => {
                const link = viewLink(r);
                return (
                  <TableRow key={r.id}>
                    <TableCell>
                      <Chip
                        size="small"
                        label={r.source === 'ssi' ? 'SSI' : 'Repair Job'}
                        color={r.source === 'ssi' ? 'secondary' : 'primary'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell><strong>{jobLabel(r)}</strong></TableCell>
                    <TableCell>{r.repair?.customer_name || '-'}</TableCell>
                    <TableCell>{r.product_name} {r.product_code && `(${r.product_code})`}</TableCell>
                    <TableCell>
                      {r.part_photo_url ? (
                        <PartPhotoImage
                          photoUrl={r.part_photo_url}
                          onClick={() => setPhotoPreview({ photoUrl: r.part_photo_url, label: r.product_name })}
                        />
                      ) : '—'}
                    </TableCell>
                    <TableCell>{r.quantity}</TableCell>
                    <TableCell>Rs. {parseFloat(r.unit_price || 0).toFixed(2)}</TableCell>
                    <TableCell>{r.approved_unit_price != null ? `Rs. ${parseFloat(r.approved_unit_price).toFixed(2)}` : '-'}</TableCell>
                    <TableCell>{r.requested_by_name}</TableCell>
                    <TableCell><Chip label={r.status} size="small" color={statusColor(r.status)} /></TableCell>
                    {canApprove && (
                      <TableCell>
                        {r.status === 'pending' && (
                          <>
                            <Button size="small" color="success" startIcon={<ApproveIcon />} onClick={() => openApprove(r)}>Approve</Button>
                            <Button size="small" color="error" startIcon={<RejectIcon />} onClick={() => handleReject(r)}>Reject</Button>
                          </>
                        )}
                      </TableCell>
                    )}
                    <TableCell>
                      {link ? (
                        <Button size="small" component={Link} to={link} startIcon={<ViewIcon />}>View</Button>
                      ) : '-'}
                    </TableCell>
                    {canDelete() && (
                      <TableCell>
                        <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={() => handleDelete(r)}>
                          Delete
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
              {requests.length === 0 && (
                <TableRow><TableCell colSpan={(canApprove ? 12 : 11) + (canDelete() ? 1 : 0)} align="center">No part requests found</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={createDialog} onClose={() => { setCreateDialog(false); resetCreateForm(); }} maxWidth="sm" fullWidth>
        <DialogTitle>Request Part for Repair Job</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField select fullWidth label="Repair Job" value={form.repair_id} onChange={(e) => setForm({ ...form, repair_id: e.target.value })}>
                {repairs.map((r) => (
                  <MenuItem key={r.id} value={r.id}>{r.grn_number || r.service_number || r.repair_number || `Repair #${r.id}`} — {r.customer_name || ''}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={8}><TextField fullWidth label="Part Name" value={form.product_name} onChange={(e) => setForm({ ...form, product_name: e.target.value })} /></Grid>
            <Grid item xs={4}><TextField fullWidth label="Part Code" value={form.product_code} onChange={(e) => setForm({ ...form, product_code: e.target.value })} /></Grid>
            <Grid item xs={4}><TextField fullWidth label="Quantity" type="number" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} /></Grid>
            <Grid item xs={4}><TextField fullWidth label="Unit Cost" type="number" value={form.unit_cost} onChange={(e) => setForm({ ...form, unit_cost: e.target.value })} /></Grid>
            <Grid item xs={4}><TextField fullWidth label="Unit Price (charge)" type="number" value={form.unit_price} onChange={(e) => setForm({ ...form, unit_price: e.target.value })} /></Grid>
            <Grid item xs={12}>
              <Button variant="outlined" component="label" startIcon={<PhotoIcon />}>
                {techOnly ? 'Upload Part Photo *' : 'Upload Part Photo'}
                <input type="file" hidden accept="image/*" onChange={handlePhotoPick} />
              </Button>
              {photoName && (
                <Typography variant="body2" sx={{ mt: 1 }} color="text.secondary">{photoName}</Typography>
              )}
              {form.part_photo && (
                <Box
                  component="img"
                  src={form.part_photo}
                  alt="preview"
                  sx={{ mt: 1, maxWidth: '100%', maxHeight: 180, borderRadius: 1, display: 'block' }}
                />
              )}
              {techOnly && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                  Photo is required for technician part requests
                </Typography>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setCreateDialog(false); resetCreateForm(); }}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate} disabled={submitting}>
            {submitting ? 'Submitting…' : 'Submit Request'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={approveDialog} onClose={() => setApproveDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Approve Part & Price</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1 }}>{selected?.product_name} × {selected?.quantity}</Typography>
          {selected?.part_photo_url && (
            <PartPhotoImage
              photoUrl={selected.part_photo_url}
              thumb={false}
              sx={{ width: '100%', maxHeight: 200, mb: 2 }}
            />
          )}
          <TextField fullWidth label="Approved Unit Price" type="number" value={approveForm.approved_unit_price} onChange={(e) => setApproveForm({ ...approveForm, approved_unit_price: e.target.value })} sx={{ mb: 2 }} />
          <TextField fullWidth label="Admin Notes" multiline rows={2} value={approveForm.admin_notes} onChange={(e) => setApproveForm({ ...approveForm, admin_notes: e.target.value })} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveDialog(false)}>Cancel</Button>
          <Button variant="contained" color="success" onClick={handleApprove}>Approve</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!photoPreview} onClose={() => setPhotoPreview(null)} maxWidth="md" fullWidth>
        <DialogTitle>{photoPreview?.label ? `Part Photo — ${photoPreview.label}` : 'Part Photo'}</DialogTitle>
        <DialogContent sx={{ textAlign: 'center', py: 2 }}>
          {photoPreview?.photoUrl && (
            <PartPhotoImage
              photoUrl={photoPreview.photoUrl}
              thumb={false}
              sx={{ maxWidth: '100%', maxHeight: '70vh', mx: 'auto' }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPhotoPreview(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PartRequests;
